import React, { forwardRef } from "react";
import { Icons } from "@/assets";
import { cx } from "@/utils/cx";
import { getAmenityIconName } from "@/utils/amenities";

interface AmenitiesCheckboxGroupProps {
  options: { label: string; value: string }[];
  value?: string | string[];
  onChange?: (val: string) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

const toSelectedValues = (value: string | string[]) => {
  const rawValue = Array.isArray(value) ? value.join(",") : value || "";
  return rawValue
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const AmenitiesCheckboxGroup = forwardRef<HTMLDivElement, AmenitiesCheckboxGroupProps>(
  ({ options, value = "", onChange, error, disabled = false, required = false }, ref) => {
    const selectedValues = toSelectedValues(value);

    const handleToggle = (optionValue: string) => {
      if (disabled) return;

      const normalizedValue = String(optionValue).trim();
      const nextValues = selectedValues.includes(normalizedValue)
        ? selectedValues.filter((item) => item !== normalizedValue)
        : [...selectedValues, optionValue];

      onChange?.(nextValues.join(","));
    };

    return (
      <div ref={ref} className="col-span-1 md:col-span-full flex flex-col gap-4">
        <p className="text-sm font-medium text-gray-110">
          Select them below
          {required && <span className="text-error ml-1">*</span>}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 2xl:grid-cols-5 gap-3">
          {options.map((option) => {
            const normalizedValue = String(option.value).trim();
            const isChecked = selectedValues.includes(normalizedValue);
            const AmenityIcon = Icons[getAmenityIconName(option.label)] || Icons.InfoIcon;

            return (
              <button
                key={option.value}
                type="button"
                role="checkbox"
                aria-checked={isChecked}
                aria-disabled={disabled}
                onClick={() => handleToggle(option.value)}
                className={cx(
                  "group flex h-12 min-w-0 items-center justify-between gap-3 rounded-[8px] border bg-white px-3 text-left transition-colors",
                  isChecked ? "border-[#78a7ff]" : "border-gray-30 hover:border-[#78a7ff]",
                  disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                  error && !isChecked && "border-[#ff424f]"
                )}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <AmenityIcon className="w-5 h-5 shrink-0 text-brand" />
                  <span className="truncate text-sm font-medium text-gray-70">{option.label}</span>
                </span>

                <span
                  className={cx(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border-[1.6px] transition-colors",
                    isChecked
                      ? "border-[#0a68ff] bg-[#0a68ff]"
                      : "border-gray-30 bg-gray-10 group-hover:border-[#78a7ff]"
                  )}
                >
                  {isChecked && <Icons.TickIcon className="h-3 w-3 text-white" />}
                </span>
              </button>
            );
          })}
        </div>

        {error && <p className="text-xs text-[#ff424f]">{error}</p>}
      </div>
    );
  }
);

AmenitiesCheckboxGroup.displayName = "AmenitiesCheckboxGroup";
